import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    FaCheckCircle,
    FaClock,
    FaExclamationTriangle,
    FaPlay,
    FaSyncAlt,
} from 'react-icons/fa';
import PageView from '../common/pageView';
import AccessLockedPanel from '../common/accessLockedPanel';
import SmallLoadingAlt from '../global/smallLoadingAlt';
import AccessService from '../../service/accessService';
import SessionService from '../../service/sessionService';
import { probabilityToAmericanOdds } from '../../utils/oddsUtils';
import globalStyles from '../globals.module.css';
import componentStyles from './models.module.css';
import {
    createGolfMatchupPrediction,
    getGolfMatchupDiscovery,
    getGolfMatchupSource,
    getLatestGolfLiveOutrights,
    getModelPredictionBySubjectKey,
    getModelPredictionJob,
    normalizeGolfMatchupPrediction
} from '../../service/modelsService';

const MODEL_TABS = [
    { key: 'outrights', label: 'Tournament Outrights' },
    { key: 'matchups', label: 'Matchups' }
];

const formatTime = (value) => {
    if (!value) return 'TBD';

    try {
        return new Date(value).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    } catch (error) {
        return 'TBD';
    }
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatProbability = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '-';
    }

    return `${(Number(value) * 100).toFixed(1)}%`;
};
const shortenIdentifier = (value) => {
    const identifier = String(value || '');
    return identifier.length > 14 ? `${identifier.slice(0, 6)}...${identifier.slice(-4)}` : identifier;
};
const formatOutcomeLabel = (outcome = {}) => {
    if (outcome.outcomeType === 'draw') {
        return 'Draw';
    }

    return outcome.playerName || 'Player';
};
const getPlayerOptionLabel = (player = {}) => (
    `${player.playerName || 'Player'}${player.country ? ` / ${player.country}` : ''}`
);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const TERMINAL_JOB_STATUSES = new Set(['complete', 'failed', 'expired', 'cancelled']);
const OUTRIGHT_SORT_OPTIONS = [
    { key: 'pWin', label: 'Win' },
    { key: 'americanOdds', label: 'Fair Odds' },
    { key: 'pMakeCut', label: 'Make Cut', requiresMakeCut: true },
    { key: 'pR1Leader', label: '(R) Leader' },
    { key: 'pTop5', label: 'Top 5' },
    { key: 'pTop10', label: 'Top 10' },
    { key: 'pTop20', label: 'Top 20' },
    { key: 'pTop30', label: 'Top 30' }
];

const getOutrightSortValue = (player, sortKey) => {
    if (sortKey === 'americanOdds') {
        return Number(player.pWin);
    }

    return Number(player[sortKey]);
};

const sortOutrightContestants = (contestants, sortKey) => (
    [...contestants].sort((a, b) => {
        const aValue = getOutrightSortValue(a, sortKey);
        const bValue = getOutrightSortValue(b, sortKey);
        const aMissing = !Number.isFinite(aValue);
        const bMissing = !Number.isFinite(bValue);

        if (aMissing && bMissing) {
            return Number(a.rank || 0) - Number(b.rank || 0);
        }

        if (aMissing) return 1;
        if (bMissing) return -1;

        return bValue - aValue;
    })
);

const getSelectedOutrightMetric = (player, sortKey, showMakeCut) => {
    switch (sortKey) {
        case 'americanOdds':
            return { label: 'Fair Odds', probability: player.pWin, odds: player.americanOdds };
        case 'pMakeCut':
            return showMakeCut
                ? { label: 'Make Cut', probability: player.pMakeCut }
                : { label: 'Win', probability: player.pWin, odds: player.americanOdds };
        case 'pR1Leader':
            return { label: 'Round Leader', probability: player.pR1Leader };
        case 'pTop5':
            return { label: 'Top 5', probability: player.pTop5 };
        case 'pTop10':
            return { label: 'Top 10', probability: player.pTop10 };
        case 'pTop20':
            return { label: 'Top 20', probability: player.pTop20 };
        case 'pTop30':
            return { label: 'Top 30', probability: player.pTop30 };
        case 'pWin':
        default:
            return { label: 'Win', probability: player.pWin, odds: player.americanOdds };
    }
};

const getOutrightTableHeaders = (sortKey, showMakeCut) => {
    const selectedMetric = getSelectedOutrightMetric({}, sortKey, showMakeCut);
    return {
        probability: selectedMetric.label === 'Fair Odds' ? 'Win' : selectedMetric.label,
        odds: selectedMetric.label === 'Fair Odds' ? 'Fair Odds' : 'Odds'
    };
};

const Models = () => {
    const sessionService = SessionService.getInstance();
    const canUseModels = AccessService.getInstance().isProOrAdmin();
    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = sessionService.getLocalSetting('models_golf_activeTab', 'outrights');
        return MODEL_TABS.some((tab) => tab.key === savedTab) ? savedTab : 'outrights';
    });

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        sessionService.setLocalSetting('models_golf_activeTab', tab);
    };

    const header = (
        <div className={componentStyles.modelsHeader}>
            <div className={componentStyles.tabRow} aria-label="Golf model views">
                {MODEL_TABS.map((tab) => (
                    <button
                        type="button"
                        key={tab.key}
                        className={`${globalStyles.controlButton} ${componentStyles.tabButton} ${activeTab === tab.key ? globalStyles.activeControl : ''}`}
                        aria-pressed={activeTab === tab.key}
                        onClick={() => handleTabChange(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        </div>
    );

    const renderContent = () => {
        if (!canUseModels) {
            return (
                <AccessLockedPanel
                    title="Models Locked"
                    message="Upgrade to Pro to access golf prediction models."
                    actionLabel="Upgrade to Pro"
                    actionView="profile"
                />
            );
        }

        switch (activeTab) {
            case 'outrights':
                return <TournamentOutrights />;
            case 'matchups':
                return <GolfMatchups />;
            default:
                return <TournamentOutrights />;
        }
    };

    return (
        <PageView
            header={header}
            contentClassName={componentStyles.modelsContent}
        >
            {renderContent()}
        </PageView>
    );
};

const TournamentOutrights = () => {
    const sessionService = SessionService.getInstance();
    const [outrights, setOutrights] = useState(null);
    const [selectedEventId, setSelectedEventId] = useState(() => (
        String(sessionService.getLocalSetting('models_golf_outrightEventId', '') || '')
    ));
    const [loadingBoard, setLoadingBoard] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [sortKey, setSortKey] = useState('pWin');

    useEffect(() => {
        let isMounted = true;

        const loadOutrights = async () => {
            setLoadingBoard(true);
            setErrorMessage('');

            try {
                const latestOutrights = await getLatestGolfLiveOutrights();
                if (isMounted) {
                    setOutrights(latestOutrights);
                    const predictions = Array.isArray(latestOutrights?.predictions)
                        ? latestOutrights.predictions
                        : [];
                    setSelectedEventId((currentEventId) => {
                        const hasCurrentEvent = predictions.some((prediction) => (
                            String(prediction.eventId) === currentEventId
                        ));
                        return hasCurrentEvent ? currentEventId : String(predictions[0]?.eventId || '');
                    });
                }
            } catch (error) {
                if (isMounted) {
                    setErrorMessage(error?.message || 'Unable to load live outright board.');
                }
            } finally {
                if (isMounted) {
                    setLoadingBoard(false);
                }
            }
        };

        loadOutrights();

        return () => {
            isMounted = false;
        };
    }, []);

    const predictions = useMemo(() => (
        Array.isArray(outrights?.predictions) ? outrights.predictions : []
    ), [outrights]);
    const board = useMemo(() => (
        predictions.find((prediction) => String(prediction.eventId) === selectedEventId) || predictions[0] || null
    ), [predictions, selectedEventId]);
    const contestants = useMemo(() => (
        Array.isArray(board?.contestants) ? board.contestants : []
    ), [board]);
    const showMakeCut = contestants.some((player) => player.pMakeCut !== null && player.pMakeCut !== undefined);
    const sortedContestants = useMemo(() => (
        sortOutrightContestants(contestants, sortKey)
    ), [contestants, sortKey]);
    const handleEventChange = (event) => {
        const eventId = event.target.value;
        setSelectedEventId(eventId);
        sessionService.setLocalSetting('models_golf_outrightEventId', eventId);
    };

    return (
        <>
            {!loadingBoard && !errorMessage && predictions.length > 0 && (
                <section className={`${globalStyles.standardSection} ${componentStyles.outrightsEventSelector}`}>
                    <label className={componentStyles.outrightsEventLabel} htmlFor="outrights-event-select">
                        Event
                    </label>
                    <select
                        id="outrights-event-select"
                        className={`${globalStyles.standardSelect} ${componentStyles.outrightsEventSelect}`}
                        value={String(board?.eventId || '')}
                        onChange={handleEventChange}
                    >
                        {predictions.map((prediction) => (
                            <option key={prediction.eventId} value={String(prediction.eventId)}>
                                {prediction.eventName || `Event ${prediction.eventId}`}
                                {prediction.tour ? ` / ${prediction.tour}` : ''}
                            </option>
                        ))}
                    </select>
                </section>
            )}

            <section className={`${globalStyles.standardSection} ${componentStyles.outrightsSummary}`}>
                <div className={componentStyles.outrightsSummaryRow}>
                    <div className={componentStyles.outrightsSummaryText}>
                        <p className={componentStyles.metaText}>
                            {board?.eventName || 'Latest Live Board'}
                            {board?.generatedAt ? ` / Generated ${formatTime(board.generatedAt)}` : ''}
                        </p>
                    </div>

                    {board && (
                        <div className={componentStyles.outrightsMetricChips}>
                            {board.tour && <CompactMetric label="Tour" value={board.tour} />}
                            <CompactMetric label="Field" value={board.fieldSize || '-'} />
                            <CompactMetric label="Sims" value={board.nSims ? Number(board.nSims).toLocaleString() : '-'} />
                        </div>
                    )}
                </div>
            </section>

            {!loadingBoard && !errorMessage && board && contestants.length > 0 && (
                <OutrightsSortBar
                    activeSort={sortKey}
                    showMakeCut={showMakeCut}
                    onChange={setSortKey}
                />
            )}

            {loadingBoard ? (
                <div className={globalStyles.loadingWrapper}>
                    <SmallLoadingAlt />
                </div>
            ) : errorMessage ? (
                <div className={globalStyles.emptyState}>{errorMessage}</div>
            ) : predictions.length === 0 || !board || contestants.length === 0 ? (
                <div className={globalStyles.emptyState}>No live outright board available yet.</div>
            ) : (
                <LiveOutrightsTable contestants={sortedContestants} sortKey={sortKey} showMakeCut={showMakeCut} />
            )}
        </>
    );
};

const OutrightsSortBar = ({ activeSort, showMakeCut, onChange }) => (
    <div className={componentStyles.outrightsSortRow} aria-label="Sort outright board">
        {OUTRIGHT_SORT_OPTIONS
            .filter((option) => showMakeCut || !option.requiresMakeCut)
            .map((option) => (
                <button
                    type="button"
                    key={option.key}
                    className={`${globalStyles.controlButton} ${globalStyles.compactButton} ${componentStyles.sortButton} ${activeSort === option.key ? globalStyles.activeControl : ''}`}
                    aria-pressed={activeSort === option.key}
                    onClick={() => onChange(option.key)}
                >
                    {option.label}
                </button>
            ))}
    </div>
);

const LiveOutrightsTable = ({ contestants, sortKey, showMakeCut }) => {
    const headers = getOutrightTableHeaders(sortKey, showMakeCut);

    return (
        <div className={componentStyles.outrightsBoard}>
            <table className={componentStyles.outrightsTable}>
                <thead>
                    <tr>
                        <th>Rank</th>
                        <th>Player</th>
                        <th>{headers.probability}</th>
                        <th>{headers.odds}</th>
                    </tr>
                </thead>
                <tbody>
                    {contestants.map((player) => {
                        const selectedMetric = getSelectedOutrightMetric(player, sortKey, showMakeCut);
                        const odds = selectedMetric.odds || probabilityToAmericanOdds(selectedMetric.probability);

                        return (
                            <tr key={player.playerId || `${player.rank}-${player.playerName}`}>
                                <td>
                                    <span className={componentStyles.rankBadge}>#{player.rank}</span>
                                </td>
                                <td className={componentStyles.outrightsPlayerCell}>{player.playerName || 'Player'}</td>
                                <td className={componentStyles.outrightsProbabilityCell}>{formatProbability(selectedMetric.probability)}</td>
                                <td className={componentStyles.outrightsOddsCell}>{odds}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const CompactMetric = ({ label, value }) => (
    <span className={componentStyles.compactMetric}>
        <span>{label}</span>
        <strong>{value}</strong>
    </span>
);

const GolfMatchups = () => {
    const runTokenRef = useRef(0);
    const sourceTokenRef = useRef(0);
    const [discovery, setDiscovery] = useState(null);
    const [source, setSource] = useState(null);
    const [loadingDiscovery, setLoadingDiscovery] = useState(true);
    const [loadingSource, setLoadingSource] = useState(false);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [selectedCourseKey, setSelectedCourseKey] = useState('');
    const [marketType, setMarketType] = useState('head_to_head');
    const [round, setRound] = useState('');
    const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
    const [jobStatus, setJobStatus] = useState('idle');
    const [jobId, setJobId] = useState('');
    const [subjectKey, setSubjectKey] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [result, setResult] = useState(null);

    const markets = Array.isArray(discovery?.markets) ? discovery.markets : [];
    const events = Array.isArray(discovery?.events) ? discovery.events : [];
    const selectedEvent = events.find((event) => event.eventId === selectedEventId) || null;
    const selectedMarket = markets.find((market) => market.market === marketType) || null;
    const requiredPlayers = selectedMarket?.requiredPlayerCount || (marketType === '3_ball' ? 3 : 2);
    const availableRounds = selectedEvent?.availableRounds || [];
    const courses = Array.isArray(source?.courses) ? source.courses : [];
    const selectablePlayers = useMemo(() => (
        Array.isArray(source?.players)
            ? source.players.filter((player) => player.modelAvailable === true)
            : []
    ), [source]);
    const activePlayerIds = selectedPlayerIds.slice(0, requiredPlayers);
    const selectedPlayers = useMemo(() => (
        activePlayerIds.map((id) => selectablePlayers.find((player) => player.playerId === id)).filter(Boolean)
    ), [activePlayerIds, selectablePlayers]);
    const canRun = (
        !loadingDiscovery &&
        !loadingSource &&
        selectedEvent?.fieldAvailable === true &&
        selectedEvent?.modelAvailable === true &&
        selectedCourseKey &&
        new Set(activePlayerIds).size === requiredPlayers &&
        selectedPlayers.length === requiredPlayers
    );

    useEffect(() => {
        let isMounted = true;

        const loadDiscovery = async () => {
            setLoadingDiscovery(true);
            setErrorMessage('');

            try {
                const nextDiscovery = await getGolfMatchupDiscovery();
                if (!isMounted) return;

                const nextMarkets = Array.isArray(nextDiscovery.markets) ? nextDiscovery.markets : [];
                const nextEvents = Array.isArray(nextDiscovery.events) ? nextDiscovery.events : [];
                const defaultMarket = nextMarkets[0]?.market || 'head_to_head';
                const defaultEvent = nextEvents.find((event) => event.fieldAvailable && event.modelAvailable) || nextEvents[0] || null;

                setDiscovery(nextDiscovery);
                setMarketType(defaultMarket);
                setSelectedEventId(defaultEvent?.eventId || '');
                setRound(String(defaultEvent?.defaultRound || defaultEvent?.currentRound || defaultEvent?.availableRounds?.[0] || ''));
            } catch (error) {
                if (isMounted) {
                    setErrorMessage(error?.message || 'Unable to load matchup setup.');
                }
            } finally {
                if (isMounted) {
                    setLoadingDiscovery(false);
                }
            }
        };

        loadDiscovery();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!selectedEventId || !round) {
            setSource(null);
            setSelectedCourseKey('');
            setSelectedPlayerIds([]);
            return;
        }

        const sourceToken = sourceTokenRef.current + 1;
        sourceTokenRef.current = sourceToken;
        resetRunState();
        setLoadingSource(true);

        const loadSource = async () => {
            try {
                const nextSource = await getGolfMatchupSource({
                    eventId: selectedEventId,
                    round: Number(round)
                });

                if (sourceToken !== sourceTokenRef.current) return;

                const nextCourses = Array.isArray(nextSource.courses) ? nextSource.courses : [];
                const defaultCourse = (
                    nextSource.defaultCourseKey ||
                    nextCourses.find((course) => course.isDefault)?.courseKey ||
                    nextCourses[0]?.courseKey ||
                    ''
                );
                const nextPlayers = Array.isArray(nextSource.players)
                    ? nextSource.players.filter((player) => player.modelAvailable === true)
                    : [];

                setSource(nextSource);
                setSelectedCourseKey(defaultCourse);
                setSelectedPlayerIds(nextPlayers.slice(0, requiredPlayers).map((player) => player.playerId));
            } catch (error) {
                if (sourceToken === sourceTokenRef.current) {
                    setSource(null);
                    setSelectedCourseKey('');
                    setSelectedPlayerIds([]);
                    setErrorMessage(error?.message || 'Unable to load players for this event.');
                }
            } finally {
                if (sourceToken === sourceTokenRef.current) {
                    setLoadingSource(false);
                }
            }
        };

        loadSource();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventId, round]);

    useEffect(() => {
        setSelectedPlayerIds((current) => {
            const allowedIds = new Set(selectablePlayers.map((player) => player.playerId));
            const next = current
                .filter((playerId) => allowedIds.has(playerId))
                .slice(0, requiredPlayers);

            if (next.length >= requiredPlayers) {
                return next;
            }

            const fill = selectablePlayers
                .map((player) => player.playerId)
                .filter((playerId) => !next.includes(playerId))
                .slice(0, requiredPlayers - next.length);

            return [...next, ...fill];
        });
    }, [requiredPlayers, selectablePlayers]);

    const updatePlayer = (index, playerId) => {
        runTokenRef.current += 1;
        setResult(null);
        setJobStatus('idle');
        setJobId('');
        setSubjectKey('');
        setErrorMessage('');
        setSelectedPlayerIds((current) => {
            const next = [...current];
            next[index] = playerId;
            return next;
        });
    };

    const resetRunState = () => {
        runTokenRef.current += 1;
        setResult(null);
        setJobStatus('idle');
        setJobId('');
        setSubjectKey('');
        setErrorMessage('');
    };

    const fetchCompletedPrediction = async (nextSubjectKey, runToken) => {
        const predictionResponse = await getModelPredictionBySubjectKey(nextSubjectKey);
        if (runToken !== runTokenRef.current) return;

        if (predictionResponse?.status === 'complete' && predictionResponse.prediction) {
            setResult(normalizeGolfMatchupPrediction(predictionResponse.prediction));
            setJobStatus('complete');
            return;
        }

        throw new Error(predictionResponse?.error || 'Prediction completed but no result was returned.');
    };

    const pollJobUntilComplete = async (nextJobId, nextSubjectKey, runToken) => {
        for (let attempt = 0; attempt < 60; attempt += 1) {
            await sleep(2500);
            if (runToken !== runTokenRef.current) return;

            const job = await getModelPredictionJob(nextJobId);
            if (runToken !== runTokenRef.current) return;

            const nextStatus = job.status || 'running';
            setJobStatus(nextStatus);

            if (!TERMINAL_JOB_STATUSES.has(nextStatus)) {
                continue;
            }

            if (nextStatus === 'complete') {
                await fetchCompletedPrediction(job.subject_key || job.subjectKey || nextSubjectKey, runToken);
                return;
            }

            throw new Error(job.error || `Prediction job ${nextStatus}.`);
        }

        throw new Error('Prediction job is still running. Try again shortly.');
    };

    const handleRunSimulation = async () => {
        if (!canRun) return;

        const runToken = runTokenRef.current + 1;
        runTokenRef.current = runToken;
        setResult(null);
        setErrorMessage('');
        setJobId('');
        setSubjectKey('');
        setJobStatus('queued');

        try {
            const request = {
                sport: 'golf',
                predictionType: 'round_matchup',
                market: marketType,
                eventId: selectedEvent.eventId,
                eventName: selectedEvent.eventName,
                round: Number(round),
                courseKey: selectedCourseKey,
                players: selectedPlayers.map((player) => ({
                    playerId: player.playerId,
                    playerName: player.playerName
                })),
                modelVersion: 'latest'
            };

            const response = await createGolfMatchupPrediction(request);
            if (runToken !== runTokenRef.current) return;

            const responseResults = response.results || {};
            const nextSubjectKey = responseResults.subjectKey || responseResults.subject_key || '';
            const nextJobId = responseResults.jobId || responseResults.job_id || '';
            const nextStatus = responseResults.status || 'queued';

            setSubjectKey(nextSubjectKey);

            if (response.httpStatus === 200 && nextStatus === 'complete' && responseResults.prediction) {
                setResult(normalizeGolfMatchupPrediction(responseResults.prediction));
                setJobStatus('complete');
                return;
            }

            if (nextJobId) {
                setJobId(nextJobId);
            }

            setJobStatus(nextStatus);

            if (response.httpStatus === 202 && nextJobId) {
                await pollJobUntilComplete(nextJobId, nextSubjectKey, runToken);
                return;
            }

            throw new Error(response.data?.error || 'Prediction request did not return a usable result.');
        } catch (error) {
            if (runToken !== runTokenRef.current) return;
            setJobStatus('failed');
            setErrorMessage(error?.response?.data?.error || error?.message || 'Prediction request failed.');
        }
    };

    return (
        <>
            <section className={`${globalStyles.standardSection} ${componentStyles.matchupBuilder}`}>
                {loadingDiscovery ? (
                    <div className={globalStyles.loadingWrapper}>
                        <SmallLoadingAlt />
                    </div>
                ) : (
                    <div className={componentStyles.matchupModeRow}>
                        {markets.map((market) => (
                            <button
                                type="button"
                                key={market.market}
                                className={`${globalStyles.controlButton} ${componentStyles.segmentButton} ${marketType === market.market ? globalStyles.activeControl : ''}`}
                                aria-pressed={marketType === market.market}
                                onClick={() => {
                                    setMarketType(market.market);
                                    resetRunState();
                                }}
                            >
                                {market.label || market.market}
                            </button>
                        ))}
                    </div>
                )}

                <div className={componentStyles.matchupContextGrid}>
                    <div className={`${globalStyles.formGroup} ${componentStyles.compactFormGroup}`}>
                        <label htmlFor="matchup-event">Event</label>
                        <select
                            id="matchup-event"
                            className={`${globalStyles.standardSelect} ${componentStyles.compactSelect}`}
                            value={selectedEventId}
                            disabled={loadingDiscovery || events.length === 0}
                            onChange={(event) => {
                                const nextEvent = events.find((option) => option.eventId === event.target.value);
                                resetRunState();
                                setSelectedEventId(event.target.value);
                                setRound(String(nextEvent?.defaultRound || nextEvent?.currentRound || nextEvent?.availableRounds?.[0] || ''));
                            }}
                        >
                            {events.length === 0 && (
                                <option value="">No events available</option>
                            )}
                            {events.map((event) => (
                                <option
                                    key={event.eventId}
                                    value={event.eventId}
                                    disabled={!event.fieldAvailable || !event.modelAvailable}
                                >
                                    {event.eventName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={`${globalStyles.formGroup} ${componentStyles.compactFormGroup}`}>
                        <label htmlFor="matchup-round">Round</label>
                        <select
                            id="matchup-round"
                            className={`${globalStyles.standardSelect} ${componentStyles.compactSelect}`}
                            value={round}
                            disabled={!selectedEvent || availableRounds.length === 0}
                            onChange={(event) => {
                                resetRunState();
                                setRound(event.target.value);
                            }}
                        >
                            {availableRounds.length === 0 && (
                                <option value="">No rounds available</option>
                            )}
                            {availableRounds.map((availableRound) => (
                                <option key={availableRound} value={String(availableRound)}>
                                    Round {availableRound}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={`${globalStyles.formGroup} ${componentStyles.compactFormGroup}`}>
                        <label htmlFor="matchup-course">Course</label>
                        <select
                            id="matchup-course"
                            className={`${globalStyles.standardSelect} ${componentStyles.compactSelect}`}
                            value={selectedCourseKey}
                            disabled={loadingSource || courses.length <= 1}
                            onChange={(event) => {
                                resetRunState();
                                setSelectedCourseKey(event.target.value);
                            }}
                        >
                            {courses.length === 0 && (
                                <option value="">No courses available</option>
                            )}
                            {courses.map((course) => (
                                <option key={`${course.round}-${course.courseKey}`} value={course.courseKey}>
                                    {course.courseName || course.courseKey}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={componentStyles.fieldInfoBar} aria-label="Selected field information">
                    <CompactMetric
                        label="Field"
                        value={loadingSource ? 'Loading' : `${selectablePlayers.length} model-ready`}
                    />
                    <CompactMetric
                        label="Source"
                        value={selectedEvent?.fieldAvailable ? 'Available' : 'Unavailable'}
                    />
                    <CompactMetric
                        label="Model"
                        value={selectedEvent?.modelAvailable ? 'Ready' : 'Unavailable'}
                    />
                    <CompactMetric
                        label="Course"
                        value={courses.find((course) => course.courseKey === selectedCourseKey)?.courseName || selectedCourseKey || '-'}
                    />
                </div>

                <div className={componentStyles.matchupPlayers}>
                    {Array.from({ length: requiredPlayers }).map((_, index) => (
                        <PlayerSelect
                            key={`${marketType}-${index}`}
                            label={String.fromCharCode(65 + index)}
                            value={selectedPlayerIds[index]}
                            players={selectablePlayers}
                            disabled={loadingSource || selectablePlayers.length === 0}
                            onChange={(value) => updatePlayer(index, value)}
                        />
                    ))}
                </div>

                {selectedEvent && (!selectedEvent.fieldAvailable || !selectedEvent.modelAvailable) && (
                    <div className={componentStyles.inlineWarning}>
                        This event is not currently available for matchup predictions.
                    </div>
                )}

                {source?.unmappedPlayers?.length > 0 && (
                    <div className={componentStyles.inlineWarning}>
                        {source.unmappedPlayers.length} field player{source.unmappedPlayers.length === 1 ? '' : 's'} could not be mapped for model selection.
                    </div>
                )}

                {!loadingSource && selectedEventId && selectablePlayers.length === 0 && (
                    <div className={componentStyles.inlineWarning}>
                        No model-ready players are available for this event and round.
                    </div>
                )}

                <div className={componentStyles.matchupActionRow}>
                    <button
                        type="button"
                        className={`${globalStyles.primaryButton} ${componentStyles.runButton}`}
                        disabled={!canRun || jobStatus === 'queued' || jobStatus === 'running'}
                        onClick={handleRunSimulation}
                    >
                        {jobStatus === 'queued' || jobStatus === 'running' ? 'Simulating...' : 'Run Simulation'}
                    </button>
                    <span className={componentStyles.jobStatus}>
                        {jobStatus === 'idle' && <><FaPlay /> Ready</>}
                        {jobStatus === 'queued' && <><FaClock /> Queued</>}
                        {jobStatus === 'running' && <><FaSyncAlt /> Running Job</>}
                        {jobStatus === 'complete' && <><FaCheckCircle /> Complete</>}
                        {jobStatus === 'failed' && <><FaExclamationTriangle /> Failed</>}
                        {jobStatus === 'expired' && <><FaExclamationTriangle /> Expired</>}
                        {jobStatus === 'cancelled' && <><FaExclamationTriangle /> Cancelled</>}
                    </span>
                    {jobId && <span className={componentStyles.jobId}>Job {shortenIdentifier(jobId)}</span>}
                    {!jobId && subjectKey && <span className={componentStyles.jobId}>{shortenIdentifier(subjectKey)}</span>}
                </div>
            </section>

            {result ? (
                <MatchupResult result={result} />
            ) : errorMessage ? (
                <div className={globalStyles.emptyState}>
                    {errorMessage}
                </div>
            ) : loadingSource ? (
                <div className={globalStyles.emptyState}>
                    Loading event field...
                </div>
            ) : null}
        </>
    );
};

const PlayerSelect = ({ label, value, players, disabled = false, onChange }) => {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const selectedPlayer = players.find((player) => player.playerId === value);
    const inputValue = isOpen ? query : (selectedPlayer ? getPlayerOptionLabel(selectedPlayer) : '');
    const normalizedQuery = query.trim().toLowerCase();
    const filteredPlayers = normalizedQuery
        ? players.filter((player) => getPlayerOptionLabel(player).toLowerCase().includes(normalizedQuery))
        : players;
    const visiblePlayers = filteredPlayers.slice(0, 8);

    const handleSelect = (playerId) => {
        const nextPlayer = players.find((player) => player.playerId === playerId);
        onChange(playerId);
        setQuery(nextPlayer ? getPlayerOptionLabel(nextPlayer) : '');
        setIsOpen(false);
    };

    return (
        <div className={componentStyles.playerSelectRow}>
            <span className={componentStyles.playerSlot}>{label}</span>
            <div className={componentStyles.playerSearchShell}>
                <input
                    className={`${globalStyles.standardInput} ${componentStyles.playerSearchInput}`}
                    type="search"
                    value={inputValue}
                    disabled={disabled}
                    placeholder={`Search player ${label}`}
                    aria-label={`Search player ${label}`}
                    onFocus={() => {
                        setQuery('');
                        setIsOpen(true);
                    }}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setIsOpen(true);
                    }}
                    onBlur={() => {
                        window.setTimeout(() => {
                            setIsOpen(false);
                            setQuery('');
                        }, 120);
                    }}
                />
                {isOpen && !disabled && (
                    <div className={componentStyles.playerSearchMenu}>
                        {visiblePlayers.length > 0 ? (
                            visiblePlayers.map((player) => (
                                <button
                                    type="button"
                                    key={player.playerId}
                                    className={`${globalStyles.controlButton} ${componentStyles.playerSearchOption} ${player.playerId === value ? `${globalStyles.activeControl} ${componentStyles.playerSearchOptionActive}` : ''}`}
                                    aria-pressed={player.playerId === value}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => handleSelect(player.playerId)}
                                >
                                    <span>{player.playerName || 'Player'}</span>
                                    {player.country && <em>{player.country}</em>}
                                </button>
                            ))
                        ) : (
                            <span className={componentStyles.playerSearchEmpty}>No players found</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const MatchupResult = ({ result }) => {
    const outcomes = Array.isArray(result.outcomes) ? result.outcomes : [];
    const playerProjections = Array.isArray(result.playerProjections) ? result.playerProjections : [];
    const projectionByPlayerId = new Map(playerProjections.map((player) => [player.playerId, player]));
    const maxProb = outcomes.length > 0
        ? Math.max(...outcomes.map((outcome) => Number(outcome.probability || 0)))
        : 0;
    const leader = outcomes.find((outcome) => Number(outcome.probability || 0) === maxProb);
    const marketLabel = result.marketType === '3_ball' ? '3-Ball' : 'Head-to-Head';

    return (
        <section className={`${globalStyles.standardSection} ${componentStyles.matchupResultSection}`}>
            <div className={componentStyles.resultSummaryRow}>
                <div className={componentStyles.resultEdge}>
                    <span>Top Outcome</span>
                    <strong>{leader ? formatOutcomeLabel(leader) : 'Outcome'}</strong>
                    <em>{formatPercent(leader?.probability)} / {leader?.fairOdds || '-'}</em>
                </div>
                <span className={componentStyles.drawBadge}>
                    {marketLabel}{result.settlement ? ` / ${result.settlement}` : ''}
                </span>
            </div>
            <p className={componentStyles.metaText}>
                {result.nSims.toLocaleString()} simulations / Round {result.round} / {result.courseKey}
                {result.predictionVersion ? ` / ${result.predictionVersion}` : ''}
            </p>
            {outcomes.length > 0 ? (
                <>
                    <div className={componentStyles.probabilityChart}>
                        {outcomes.map((outcome) => {
                            const probability = Number(outcome.probability || 0);
                            const isLeader = probability === maxProb;

                            return (
                                <div className={componentStyles.probabilityRow} key={`${outcome.outcomeType}-${outcome.playerId || 'draw'}`}>
                                    <div className={componentStyles.probabilityLabel}>
                                        <strong>{formatOutcomeLabel(outcome)}</strong>
                                        <span>{formatPercent(probability)} / {outcome.fairOdds || '-'}</span>
                                    </div>
                                    <div className={componentStyles.barTrack}>
                                        <div
                                            className={`${componentStyles.barFill} ${isLeader ? componentStyles.leadingBar : ''}`}
                                            style={{ width: `${Math.min(Math.max(probability, 4), 100)}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className={componentStyles.resultGrid}>
                        {outcomes.map((outcome) => {
                            const projection = projectionByPlayerId.get(outcome.playerId);
                            const componentEntries = outcome.components
                                ? Object.entries(outcome.components)
                                : [];

                            return (
                                <article className={componentStyles.resultPlayerCard} key={`${outcome.outcomeType}-${outcome.playerId || 'draw'}`}>
                                    <div className={componentStyles.resultPlayerTopRow}>
                                        <h4>{formatOutcomeLabel(outcome)}</h4>
                                        <span>
                                            {formatPercent(outcome.probability)}
                                            {' / '}
                                            {outcome.fairOdds || '-'}
                                        </span>
                                    </div>
                                    <div className={componentStyles.resultMetricStrip}>
                                        <CompactMetric label="Type" value={outcome.outcomeType.replaceAll('_', ' ') || '-'} />
                                        {projection && (
                                            <>
                                                <CompactMetric label="Mu" value={projection.mu.toFixed(2)} />
                                                <CompactMetric label="Sigma" value={projection.sigma.toFixed(2)} />
                                            </>
                                        )}
                                        {componentEntries.map(([key, value]) => (
                                            <CompactMetric
                                                key={key}
                                                label={key.replaceAll('_', ' ')}
                                                value={formatPercent(value)}
                                            />
                                        ))}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </>
            ) : (
                <div className={globalStyles.emptyState}>
                    Prediction completed without outcome rows.
                </div>
            )}
            {playerProjections.length > 0 && (
                <div className={componentStyles.projectionContext}>
                    {playerProjections.map((player) => (
                        <span key={player.playerId}>
                            {player.playerName}: {player.mu.toFixed(2)} mu / {player.sigma.toFixed(2)} sigma
                        </span>
                    ))}
                </div>
            )}
        </section>
    );
};

export default Models;
